import type { ScheduleItem } from '../../domain/entities/schedule.ts';
import type { MemberKind } from '../../ports/auth-port.ts';
import type { DataPort } from '../../ports/data-port.ts';
import type { Query, Unsubscribe } from '../../ports/query.ts';

/**
 * Reading a job's schedule items — as a query the rules can approve.
 *
 * This exists because of a hole found in the `scheduleItems` list rule,
 * the same one P3-04f found in files: the rule gated the query on the
 * module level and the published flag and never looked at the document, so
 * a query that asked for everything got everything. A sub or a homeowner
 * on the job could list internal-only items and other people's private
 * ones. Nothing in the product did that, which is what made it easy to
 * miss — and `sub-schedule.ts` was reading every item and filtering in
 * memory, which is the definition of relying on the client to hide data.
 *
 * The rule now checks each returned document, so every caller has to ask
 * only for what it may see. The query shape is keyed by `MemberKind` here,
 * in one place, so a screen cannot invent a query the rule refuses.
 */
export interface ScheduleReadContext {
  readonly data: DataPort;
  readonly companyId: string;
  readonly jobId: string;
  readonly uid: string;
}

function repo(context: ScheduleReadContext) {
  return context.data.collection<ScheduleItem>(
    { companyId: context.companyId, jobId: context.jobId },
    'scheduleItems',
  );
}

/**
 * The queries whose union is "the schedule items on this job you may see".
 *
 * Soonest first, always. Every caller wanted that ordering anyway, and
 * fixing it here means one set of composite indexes rather than one per
 * screen — an index that does not exist is a query that fails in
 * production and nowhere else.
 */
export function visibleScheduleQueries(
  uid: string,
  kind: MemberKind = 'internal',
): readonly Query[] {
  const soonestFirst = [{ field: 'startDate', direction: 'asc' as const }];

  if (kind === 'sub' || kind === 'client') {
    const audience = kind === 'sub' ? 'visibility.subs' : 'visibility.owner';
    // One query, not two: an external member has no private items of their
    // own, because they cannot write a schedule item at all.
    return [
      {
        where: [
          { field: 'visibility.private', op: '==', value: false },
          { field: audience, op: '==', value: true },
        ],
        orderBy: soonestFirst,
      },
    ];
  }

  return [
    {
      where: [{ field: 'visibility.private', op: '==', value: false }],
      orderBy: soonestFirst,
    },
    {
      where: [
        { field: 'visibility.private', op: '==', value: true },
        { field: 'createdBy', op: '==', value: uid },
      ],
      orderBy: soonestFirst,
    },
  ];
}

/** One list from several, soonest first, with no item counted twice. */
export function mergeScheduleItems(...pages: readonly (readonly ScheduleItem[])[]): ScheduleItem[] {
  const byId = new Map<string, ScheduleItem>();
  for (const page of pages) {
    for (const item of page) byId.set(item.id, item);
  }
  // Start date, then id — **exactly what the queries themselves return**,
  // because the document key is the tiebreak Firestore applies and ids are
  // ULIDs, so a tie falls back to creation order.
  //
  // It sorted by `endDate` before `id` at first, which seemed harmless and
  // was not: two items starting the same Monday swapped places on the
  // schedule, the shorter one first. A merge that orders differently from
  // the query it merges is a reordering nobody asked for, and the Gantt
  // drag test found it by dropping on the wrong row.
  return [...byId.values()].sort(
    (a, b) => a.startDate.localeCompare(b.startDate) || a.id.localeCompare(b.id),
  );
}

/**
 * Every item this caller may see, soonest first.
 *
 * Named apart from `listScheduleItems` in `save-schedule-item.ts`, which
 * is the internal-only convenience every schedule use case already calls
 * and which now delegates here.
 */
export async function listVisibleScheduleItems(
  context: ScheduleReadContext,
  kind: MemberKind = 'internal',
): Promise<ScheduleItem[]> {
  const pages = await Promise.all(
    visibleScheduleQueries(context.uid, kind).map((query) => repo(context).list(query)),
  );
  return mergeScheduleItems(...pages.map((page) => page.items));
}

/**
 * The same, live.
 *
 * Reports nothing until every query has answered, for the reason recorded
 * on `subscribeToJobFiles`: emitting after the first would show a list
 * that is briefly missing your own private items, and a reader cannot tell
 * "not loaded yet" from "not there".
 */
export function subscribeToScheduleItems(
  context: ScheduleReadContext,
  onChange: (items: readonly ScheduleItem[]) => void,
  onError: (cause: Error) => void,
  kind: MemberKind = 'internal',
): Unsubscribe {
  const queries = visibleScheduleQueries(context.uid, kind);
  const pages: (readonly ScheduleItem[] | undefined)[] = queries.map(() => undefined);

  const emit = () => {
    const answered: (readonly ScheduleItem[])[] = [];
    for (const page of pages) {
      if (page === undefined) return;
      answered.push(page);
    }
    onChange(mergeScheduleItems(...answered));
  };

  const stops = queries.map((query, index) =>
    repo(context).subscribe(
      query,
      (page) => {
        pages[index] = page.items;
        emit();
      },
      onError,
    ),
  );

  return () => {
    for (const stop of stops) stop();
  };
}
